import { Capacitor } from "@capacitor/core";
import { AudioPlayer } from "@mediagrid/capacitor-native-audio";
import { API } from "@/lib/api";

/** Native (Capacitor/Android) engine for the live radio: Media3/ExoPlayer in a
 *  foreground MediaSessionService → persistent notification, lock-screen controls,
 *  audio focus (calls / other apps / headphones) handled by the OS. */

export const isNativeApp = () => Capacitor.isNativePlatform();

const ID = "fi-radio-live";
const APP_TITLE = "Fala Itapira e Região";
const LOGO_URL = `${API}/brand/logo.png`;

let created = false;
let listeners = [];

const withBuster = (src) => `${src}${src.includes("?") ? "&" : "?"}_r=${Date.now()}`;

export async function nativeCreate({ src, program, host }, { onStatus, onEnd, onReady }) {
  if (created) {
    await AudioPlayer.destroy({ audioId: ID }).catch(() => {});
    created = false;
  }
  await AudioPlayer.create({
    audioId: ID,
    audioSource: withBuster(src),
    friendlyTitle: APP_TITLE,
    artistName: host ? `${program} · com ${host}` : program,
    albumTitle: "Rádio ao vivo",
    artworkSource: LOGO_URL,
    useForNotification: true,
    isBackgroundMusic: false,
    loop: false,
    showSeekBackward: false,
    showSeekForward: false,
    metadataUpdateUrl: `${API}/radio/now-playing`,
    metadataUpdateInterval: 30,
  });
  created = true;
  listeners = await Promise.all([
    AudioPlayer.onPlaybackStatusChange({ audioId: ID }, (r) => onStatus?.(r.status)),
    AudioPlayer.onAudioEnd({ audioId: ID }, () => onEnd?.()),
    AudioPlayer.onAudioReady({ audioId: ID }, () => onReady?.()),
  ]);
  await AudioPlayer.initialize({ audioId: ID });
}

export const nativePlay = () => AudioPlayer.play({ audioId: ID });
export const nativePause = () => AudioPlayer.pause({ audioId: ID });
export const nativeSetVolume = (volume) => AudioPlayer.setVolume({ audioId: ID, volume });

export async function nativeIsPlaying() {
  const r = await AudioPlayer.isPlaying({ audioId: ID });
  return !!r?.isPlaying;
}

export async function nativePosition() {
  const r = await AudioPlayer.getCurrentTime({ audioId: ID });
  return Number(r?.currentTime || 0);
}

/** Hard reconnect: swap source (cache-buster) and play again. */
export async function nativeReconnect(src) {
  await AudioPlayer.changeAudioSource({ audioId: ID, source: withBuster(src) });
  await AudioPlayer.play({ audioId: ID });
}

export async function nativeStop() {
  if (!created) return;
  await AudioPlayer.stop({ audioId: ID }).catch(() => {});
  await AudioPlayer.destroy({ audioId: ID }).catch(() => {});
  created = false;
  listeners = [];
}
